import { useMemo, useState } from "react";
import "./MessageLogsPage.css";
import "../components/MessagingButtons.css";
import "../components/MessagingPage.css";
import "../components/ClientSelectableTable.css";
import { useAuth } from "../../../hooks/useAuth";
import { useMessageLogs } from "../../../hooks/useMessageLogs";
import { useClients } from "../../../hooks/useClients";
import {
  MessageChannel,
  MessageType,
  MESSAGE_CHANNEL_LABELS,
  MESSAGE_LOG_STATUS_LABELS,
  MESSAGE_TYPE_LABELS,
} from "../../../models/MessageModels";
import MessageStatusBadge from "../components/MessageStatusBadge";
import { formatDateTime } from "../../../utils/DateTime";

const truncate = (text: string, max: number): string =>
  text.length > max ? `${text.slice(0, max)}…` : text;

const MessageLogsPage = () => {
  const { user } = useAuth();
  const organizationId = user?.activeOrganization?.organizationId;

  const { logs, loading, error, reload } = useMessageLogs(organizationId);
  const { clients, loading: clientsLoading } = useClients(organizationId, {
    pageSize: 1000,
  });

  const [messageType, setMessageType] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const clientNameMap = useMemo(
    () => new Map(clients.map((client) => [client.id, client.fullName])),
    [clients],
  );

  const filteredLogs = useMemo(() => {
    const from = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : null;
    const to = toDate ? new Date(`${toDate}T23:59:59.999`).getTime() : null;

    return logs
      .filter((log) => {
        if (messageType && log.messageType !== Number(messageType)) return false;
        if (status && log.status !== Number(status)) return false;
        const at = new Date(log.createdAt).getTime();
        if (from !== null && at < from) return false;
        if (to !== null && at > to) return false;
        return true;
      })
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [logs, messageType, status, fromDate, toDate]);

  const hasFilters = messageType !== "" || status !== "" || fromDate !== "" || toDate !== "";

  const resetFilters = () => {
    setMessageType("");
    setStatus("");
    setFromDate("");
    setToDate("");
  };

  return (
    <div className="msg-page">
      <div className="msg-page__header">
        <div>
          <h2>Message Logs</h2>
          <p>
            Audit trail of every message sent for{" "}
            {user?.activeOrganization?.name ?? "your organization"}.
          </p>
        </div>
      </div>

      <div className="msg-log-filters">
        <label className="msg-log-filters__item">
          <span>Message type</span>
          <select
            value={messageType}
            onChange={(e) => setMessageType(e.target.value)}
          >
            <option value="">All types</option>
            {Object.entries(MESSAGE_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>

        <label className="msg-log-filters__item">
          <span>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {Object.entries(MESSAGE_LOG_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>

        <label className="msg-log-filters__item">
          <span>From</span>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </label>

        <label className="msg-log-filters__item">
          <span>To</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </label>

        {hasFilters && (
          <button className="msg-btn msg-btn--secondary" onClick={resetFilters}>
            Reset
          </button>
        )}
      </div>

      {loading || clientsLoading ? (
        <div className="msg-table-empty">Loading message logs…</div>
      ) : error ? (
        <div className="msg-table-empty">
          <p>{error}</p>
          <button className="msg-btn msg-btn--secondary" onClick={reload}>
            Retry
          </button>
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="msg-table-empty">
          {logs.length === 0
            ? "No messages have been sent yet."
            : "No logs match the current filters."}
        </div>
      ) : (
        <div className="msg-table__wrap">
          <table className="msg-table__grid msg-logs-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Client</th>
                <th>Phone</th>
                <th>Channel</th>
                <th>Type</th>
                <th>Status</th>
                <th>Error</th>
                <th>Body</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((log) => (
                <tr key={log.id}>
                  <td className="msg-logs-table__muted">
                    {formatDateTime(log.sentAt || log.createdAt)}
                  </td>
                  <td className="msg-logs-table__name">
                    {clientNameMap.get(log.clientId) ?? "Unknown client"}
                  </td>
                  <td>{log.phone || "—"}</td>
                  <td>
                    {MESSAGE_CHANNEL_LABELS[log.channel as MessageChannel] ?? "—"}
                  </td>
                  <td>
                    {MESSAGE_TYPE_LABELS[log.messageType as MessageType] ?? "—"}
                  </td>
                  <td>
                    <MessageStatusBadge status={log.status} />
                  </td>
                  <td className="msg-logs-table__muted">
                    {log.errorMessage || "—"}
                  </td>
                  <td className="msg-logs-table__body">
                    <span title={log.body}>{truncate(log.body, 100)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MessageLogsPage;